/* eslint-disable react/jsx-props-no-spreading
   -- DataLayer spreads props to child data layer components */
import React from 'react';
import PropTypes from 'prop-types';
import DataManager from '@americanexpress/data-manager';
import SharedScripts, { Adobe } from '@americanexpress/shared-scripts';
import { AmexDDLRegister, getAmexDataManagerConfig } from 'axp-data-manager-utils';
import { DDLRegister } from '@americanexpress/react-ddl';
import { getDeviceType } from '../utils';
import UpdateViewDDL from './UpdateViewDDL';
import OneXp from './OneXp';

export const generateAnalyticsEvent = ({ type, elementProp, reactlytics = {} }) => {
  const { detail, element } = reactlytics;
  return {
    element: element || elementProp,
    type: reactlytics.type || type,
    detail,
    meta: {
      ...reactlytics.meta,
    },
  };
};

export const getTargetNodeName = (syntheticEvent = {}) => {
  const event = syntheticEvent.nativeEvent || {};
  const element = event.target || {};
  return element.nodeName;
};

export const customHandlerCreators = () => ({
  onClick: (track, handler, props) => (syntheticEvent) => {
    const { type = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = type || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'click' });
    track({ event });
  },
  onSubmit: (track, handler, props) => (syntheticEvent) => {
    const { id = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = id || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'submit' });
    track({ event });
  },
  onDismiss: (track, handler, props) => (syntheticEvent) => {
    const { label = undefined, type = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = label || type || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'dismiss' });
    track({ event });
  },
  onChange: (track, handler, props) => (syntheticEvent) => {
    const { id = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = id || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'change' });
    track({ event });
  },
  onFocus: (track, handler, props) => (syntheticEvent) => {
    const { id = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = id || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'focus' });
    track({ event });
  },
  onBlur: (track, handler, props) => (syntheticEvent) => {
    const { id = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = id || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'blur' });
    track({ event });
  },
  default: (track, handler, props) => (syntheticEvent) => {
    const { reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const event = generateAnalyticsEvent({ elementProp: defaultElement, reactlytics, type: reactlytics.type || 'event' });
    track({ event });
  },
});

function DataLayer({ children, locale, location }) {
  const obj = global.BROWSER ? {
    initialDDL: {
      historyLimit: 700,
      ddl: {
        diff: ['events'],
        version: '1.7.0',
        charset: 'UTF-8',
        user: {},
        events: [],
        components: {},
        campaigns: {},
        timestamp: new Date().toISOString(),
        device: {
          userAgent: window.navigator.userAgent,
          width: {
            px: window.screen.width,
          },
          height: {
            px: window.screen.height,
          },
          channel: getDeviceType(navigator.userAgent),
        },

        view: {
          country: locale.split('-')[1],
          language: locale.split('-')[0],
          locale,
          location: window.location.href,
          timestamp: new Date().toISOString(),
          origin: window.location.hostname,
          title: 'American Express - American Express',
          viewId: window.location.pathname,
        },
      },
    },
  } : {};

  return (
    <DataManager
      {...getAmexDataManagerConfig({
        ...obj,
        reactlyticsProps: {
          handlerCreators: customHandlerCreators(),
        },
      })}
    >
      <SharedScripts
        name="one-stream-data-handler"
        windowCheck="oneStreamData"
        version="^0.1.1"
      />
      <AmexDDLRegister />
      <DDLRegister name="user.authenticated" value={false} />
      <UpdateViewDDL locale={locale} />
      <Adobe async={true} version="^1.0.0" />
      <OneXp locale={locale} pathname={location?.pathname}>
        {children}
      </OneXp>
    </DataManager>
  );
}

DataLayer.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  locale: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

export default DataLayer;
/* eslint-enable react/jsx-props-no-spreading */
